import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import uploadService from '../services/uploadService';
import { useAuth } from './AuthContext';

const UploadContext = createContext(null);

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
};

export const UploadProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [activeUploads, setActiveUploads] = useState([]);
    const [hasActiveUploads, setHasActiveUploads] = useState(false);

    const checkIncompleteUploads = useCallback(async () => {
        try {
            const result = await uploadService.getIncompleteUploads();
            if (result.success && result.uploads.length > 0) {
                setActiveUploads(result.uploads);
                setHasActiveUploads(true);
            }
        } catch (error) {
            console.error('Error checking incomplete uploads:', error);
        }
    }, []);

    // Check for incomplete uploads on mount
    useEffect(() => {
        if (isAuthenticated) {
            checkIncompleteUploads();
        }
    }, [isAuthenticated, checkIncompleteUploads]);

    const addUpload = useCallback((upload) => {
        setActiveUploads(prev => [...prev, upload]);
        setHasActiveUploads(true);
    }, []);

    const updateUploadProgress = useCallback((uploadId, progress) => {
        setActiveUploads(prev =>
            prev.map(upload =>
                upload.uploadId === uploadId
                    ? { ...upload, progress }
                    : upload
            )
        );
    }, []);

    const removeUpload = useCallback((uploadId) => {
        setActiveUploads(prev => {
            const filtered = prev.filter(upload => upload.uploadId !== uploadId);
            setHasActiveUploads(filtered.length > 0);
            return filtered;
        });
    }, []);

    const completeUpload = useCallback(async (uploadId) => {
        try {
            await uploadService.completeUpload(uploadId);
            removeUpload(uploadId);
            return { success: true };
        } catch (error) {
            console.error('Error completing upload:', error);
            return { success: false, error: error.message };
        }
    }, [removeUpload]);

    const failUpload = useCallback(async (uploadId, errorDetails) => {
        try {
            await uploadService.failUpload(uploadId, errorDetails);
            removeUpload(uploadId);
            return { success: true };
        } catch (error) {
            console.error('Error failing upload:', error);
            return { success: false, error: error.message };
        }
    }, [removeUpload]);

    const canLogout = useCallback(() => {
        return !hasActiveUploads;
    }, [hasActiveUploads]);

    const value = {
        activeUploads,
        hasActiveUploads,
        addUpload,
        updateUploadProgress,
        removeUpload,
        completeUpload,
        failUpload,
        canLogout,
        checkIncompleteUploads
    };

    return (
        <UploadContext.Provider value={value}>
            {children}
        </UploadContext.Provider>
    );
};

export default UploadContext;
